import {forwardRef, InputHTMLAttributes, Ref, useRef} from 'react';
import ICON_DELETE_BTN_LIGHT from '../../../public/static/v4/search/icon-keywords-delete-btn-light.svg';
import ICON_DELETE_BTN from '../../../public/static/v4/search/icon-keywords-delete-btn.svg';
import Text from '../../common/component/Text';
import {KeyboardKeyType} from '../../common/constant';
import {getKeyboardKeyType} from '../../common/function';
import useTheme from '../../common/hook/useTheme';
import {setRefs} from '../function';

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  onClear?: (e: EventObject) => void;
  onKeyUpEnter?: () => void;
  className?: string;
  inputClassName?: string;
  descText?: string;
  inputVariant?: IInputVariant;
}

function InputV4(
  {
    className,
    onClear,
    onKeyUpEnter,
    descText,
    inputClassName,
    type,
    value,
    readOnly,
    disabled,
    inputVariant = 'default',
    ...rest
  }: IProps,
  ref: Ref<HTMLInputElement>
) {
  const {isLightMode} = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const onKeyUp = (e: KeyboardEventObject) => {
    const keyType = getKeyboardKeyType(e);
    if (keyType === KeyboardKeyType.Enter) {
      onKeyUpEnter?.();
    } else if (keyType === KeyboardKeyType.Space) {
      if (type === 'email' || type === 'password') {
        e.preventDefault();
      }
    }
  };

  return (
    <>
      <div className={cn('relative', className)}>
        <input
          ref={el => setRefs(el, inputRef, ref)}
          type={type}
          className={cn(inputClassName, INPUT_DEFAULT_CN, {
            'pr-18': inputVariant === 'default',
            'pr-40': inputVariant === 'clearButton',
            'pr-63': inputVariant === 'count',
          })}
          value={value}
          readOnly={readOnly}
          disabled={disabled}
          onKeyUp={onKeyUp}
          {...rest}
        />
        {inputVariant === 'clearButton' &&
          !!value &&
          !readOnly &&
          !disabled && (
            <img
              src={isLightMode ? ICON_DELETE_BTN_LIGHT : ICON_DELETE_BTN}
              className="absolute right-12 top-1/2 -translate-y-1/2 cursor-pointer"
              width={20}
              height={20}
              alt="Clear keywords"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                inputRef.current?.focus();
                onClear?.(e);
              }}
            />
          )}
        {inputVariant === 'count' && (
          <div
            className={cn('absolute right-16 top-1/2 -translate-y-1/2 flex', {
              'opacity-30': disabled,
            })}
          >
            <Text className="s14-medium-grey15">{String(value).length}</Text>
            <Text className="s14-medium-grey07">{`/${rest.maxLength}`}</Text>
          </div>
        )}
      </div>
      {descText && (
        <Text className="s12-regular-grey06 mt-10 ml-7">{descText}</Text>
      )}
    </>
  );
}

type IInputVariant = 'clearButton' | 'count' | 'default';

const INPUT_DEFAULT_CN = cn(
  'bg-grey-01 pl-18 py-16 rounded-8 placeholder-grey-08 outline-none',
  'light:bg-white light:placeholder-grey-06',
  'read-only:bg-transparent read-only:border read-only:border-solid read-only:border-grey-02 read-only:light:border-grey-03',
  'disabled:text-grey-03 disabled:placeholder-grey-03 disabled:bg-transparent',
  'light:disabled:text-grey-03 light:disabled:placeholder-grey-03 light:disabled:bg-transparent'
);

export default forwardRef(InputV4);
